import type { Card, GameState, PileId } from "../game/types.ts";
import { colorOf } from "../game/types.ts";
import type { Layout } from "./layout.ts";

const RANK_LABEL = ["", "A", "2", "3", "4", "5", "6", "7", "8", "9", "10", "J", "Q", "K"];
const MAX_VISIBLE_WASTE = 3;
const CARD_TRANSITION_MS = 180;
const STOCK_TO_WASTE_ELEVATION_MS = 260;

export interface RenderTargets {
  board: HTMLElement;
  cardEls: Map<number, HTMLElement>; // cardId → element
  placeholderEls: Map<string, HTMLElement>; // pileKey → placeholder element
  stockBadge: HTMLElement | null;
  deferredRemoveTimers: Map<number, number>;
}

export function pileKey(p: PileId): string {
  return `${p.kind}:${p.index}`;
}

export function createRenderTargets(board: HTMLElement): RenderTargets {
  return {
    board,
    cardEls: new Map(),
    placeholderEls: new Map(),
    stockBadge: null,
    deferredRemoveTimers: new Map(),
  };
}

export function render(
  state: GameState,
  layout: Layout,
  targets: RenderTargets,
  selected: Set<number>,
): void {
  const { cardEls, placeholderEls, board } = targets;
  ensurePlaceholders(state, layout, board, placeholderEls);

  // Set CSS vars on the board element so card sizes follow layout.
  board.style.setProperty("--card-w", `${layout.cardW}px`);
  board.style.setProperty("--card-h", `${layout.cardH}px`);

  const seenIds = new Set<number>();

  const placeCard = (
    card: Card,
    x: number,
    y: number,
    z: number,
    pile: PileId,
    indexInPile: number,
    pileSize: number,
  ): void => {
    seenIds.add(card.id);
    let el = cardEls.get(card.id);
    if (!el) {
      el = createCardEl(card);
      board.appendChild(el);
      cardEls.set(card.id, el);
    }
    const deferredRemove = targets.deferredRemoveTimers.get(card.id);
    if (deferredRemove != null) {
      window.clearTimeout(deferredRemove);
      targets.deferredRemoveTimers.delete(card.id);
    }
    const interruptedInvalidTap =
      el.dataset.invalidTap === "1" ? freezeAndCancelAnimations(el) : false;
    const previousPile = el.dataset.pile;
    const previousRendered = el.dataset.rendered;
    const isStockToWaste =
      previousPile === pileKey({ kind: "stock", index: 0 }) &&
      pile.kind === "waste" &&
      previousRendered === "back" &&
      card.faceUp;
    updateCardEl(el, card);
    const targetTransform = `translate(${x}px, ${y}px)`;
    if (interruptedInvalidTap) {
      slideFromFrozenTransform(el, targetTransform);
    } else {
      el.style.transform = targetTransform;
    }
    el.style.zIndex = String(z);
    el.dataset.pile = pileKey(pile);
    el.dataset.pileIndex = String(indexInPile);
    el.dataset.pileSize = String(pileSize);
    el.classList.toggle("selected", selected.has(card.id));
    if (isStockToWaste) elevateStockToWaste(el);
  };

  // Tableau
  for (let i = 0; i < state.tableau.length; i++) {
    const col = state.tableau[i]!;
    const origin = layout.pilePositions.tableau[i]!;
    let y = origin.y;
    for (let k = 0; k < col.length; k++) {
      const card = col[k]!;
      placeCard(
        card,
        origin.x,
        y,
        100 + k,
        { kind: "tableau", index: i },
        k,
        col.length,
      );
      y += card.faceUp ? layout.stackGapUp : layout.stackGapDown;
    }
  }

  // Foundation — cards stack tightly at the slot (no visual offset).
  for (let i = 0; i < state.foundation.length; i++) {
    const pile = state.foundation[i]!;
    const origin = layout.pilePositions.foundation[i]!;
    for (let k = 0; k < pile.length; k++) {
      const card = pile[k]!;
      placeCard(
        card,
        origin.x,
        origin.y,
        100 + k,
        { kind: "foundation", index: i },
        k,
        pile.length,
      );
    }
  }

  // Stock (face-down stacked at same position)
  {
    const origin = layout.pilePositions.stock;
    for (let k = 0; k < state.stock.length; k++) {
      const card = state.stock[k]!;
      placeCard(
        card,
        origin.x,
        origin.y,
        100 + k,
        { kind: "stock", index: 0 },
        k,
        state.stock.length,
      );
    }
    // Remaining count badge on top of the stock pile
    if (!targets.stockBadge) {
      targets.stockBadge = document.createElement("div");
      targets.stockBadge.className = "stock-badge";
      board.appendChild(targets.stockBadge);
    }
    const badge = targets.stockBadge;
    if (state.stock.length > 0) {
      badge.textContent = String(state.stock.length);
      badge.style.display = "block";
      badge.style.transform = `translate(${origin.x}px, ${origin.y}px)`;
      badge.style.zIndex = String(100 + state.stock.length + 1);
    } else {
      badge.style.display = "none";
    }
    // Recycle hint on the stock placeholder when stock is empty but waste has cards.
    const stockPlaceholder = placeholderEls.get(pileKey({ kind: "stock", index: 0 }));
    if (stockPlaceholder) {
      const canRecycle = state.stock.length === 0 && state.waste.length > 0;
      stockPlaceholder.classList.toggle("can-recycle", canRecycle);
      stockPlaceholder.textContent = canRecycle ? "↻" : "";
    }
  }

  // Waste fans out up to three cards in both draw modes. Only the top card
  // remains draggable because input uses the real pile index and pile size.
  {
    const origin = layout.pilePositions.waste;
    const visibleStart = Math.max(0, state.waste.length - MAX_VISIBLE_WASTE);
    for (let k = visibleStart; k < state.waste.length; k++) {
      const card = state.waste[k]!;
      const visibleIndex = k - visibleStart;
      placeCard(
        card,
        origin.x + visibleIndex * layout.wasteOffsetX,
        origin.y,
        100 + k,
        { kind: "waste", index: 0 },
        k,
        state.waste.length,
      );
    }
  }

  const hiddenWasteIds = new Set(
    state.waste
      .slice(0, Math.max(0, state.waste.length - MAX_VISIBLE_WASTE))
      .map((card) => card.id),
  );

  // Remove DOM elements for cards no longer visible. Waste cards that just
  // fell out of the three-card fan stay until the shift animation finishes.
  for (const [id, el] of cardEls) {
    if (!seenIds.has(id)) {
      if (hiddenWasteIds.has(id) && el.dataset.pile === pileKey({ kind: "waste", index: 0 })) {
        scheduleDeferredRemove(targets, id, el);
      } else {
        const deferredRemove = targets.deferredRemoveTimers.get(id);
        if (deferredRemove != null) {
          window.clearTimeout(deferredRemove);
          targets.deferredRemoveTimers.delete(id);
        }
        el.remove();
        cardEls.delete(id);
      }
    }
  }
}

function scheduleDeferredRemove(
  targets: RenderTargets,
  id: number,
  el: HTMLElement,
): void {
  if (targets.deferredRemoveTimers.has(id)) return;
  const timer = window.setTimeout(() => {
    el.remove();
    targets.cardEls.delete(id);
    targets.deferredRemoveTimers.delete(id);
  }, CARD_TRANSITION_MS + 40);
  targets.deferredRemoveTimers.set(id, timer);
}

function freezeAndCancelAnimations(el: HTMLElement): boolean {
  const visualTransform = getComputedStyle(el).transform;
  el.getAnimations().forEach((animation) => animation.cancel());
  if (visualTransform === "none") return false;
  el.style.transition = "none";
  el.style.transform = visualTransform;
  void el.offsetWidth;
  return true;
}

function slideFromFrozenTransform(el: HTMLElement, targetTransform: string): void {
  el.dataset.pendingTransform = targetTransform;
  window.requestAnimationFrame(() => {
    if (el.dataset.pendingTransform !== targetTransform) return;
    delete el.dataset.pendingTransform;
    el.style.transition = "";
    el.style.transform = targetTransform;
  });
}

function ensurePlaceholders(
  state: GameState,
  layout: Layout,
  board: HTMLElement,
  placeholderEls: Map<string, HTMLElement>,
): void {
  const ensure = (key: string, x: number, y: number) => {
    let el = placeholderEls.get(key);
    if (!el) {
      el = document.createElement("div");
      el.className = "card placeholder";
      el.dataset.placeholder = key;
      board.appendChild(el);
      placeholderEls.set(key, el);
    }
    el.style.transform = `translate(${x}px, ${y}px)`;
    el.style.zIndex = "1";
  };

  for (let i = 0; i < state.tableau.length; i++) {
    const p = layout.pilePositions.tableau[i]!;
    ensure(pileKey({ kind: "tableau", index: i }), p.x, p.y);
  }
  for (let i = 0; i < state.foundation.length; i++) {
    const p = layout.pilePositions.foundation[i]!;
    ensure(pileKey({ kind: "foundation", index: i }), p.x, p.y);
  }
  ensure(
    pileKey({ kind: "stock", index: 0 }),
    layout.pilePositions.stock.x,
    layout.pilePositions.stock.y,
  );
  const wastePlaceholder = placeholderEls.get(pileKey({ kind: "waste", index: 0 }));
  if (wastePlaceholder) {
    wastePlaceholder.remove();
    placeholderEls.delete(pileKey({ kind: "waste", index: 0 }));
  }
}

function createCardEl(card: Card): HTMLElement {
  const el = document.createElement("div");
  el.dataset.cardId = String(card.id);
  el.className = "card";
  return el;
}

function updateCardEl(el: HTMLElement, card: Card): void {
  el.classList.toggle("joker", card.isJoker === true);
  el.classList.toggle("face-down", !card.faceUp);
  el.classList.toggle(
    "red",
    card.faceUp && !card.isJoker && colorOf(card.suit) === "red",
  );
  el.classList.toggle(
    "black",
    card.faceUp && !card.isJoker && colorOf(card.suit) === "black",
  );

  const renderedKey = card.faceUp
    ? card.isJoker
      ? "joker"
      : `${card.rank}${card.suit}`
    : "back";
  if (el.dataset.rendered === renderedKey) return;
  el.dataset.rendered = renderedKey;

  if (!card.faceUp) {
    el.innerHTML = "";
    return;
  }
  if (card.isJoker) {
    el.innerHTML = `
      <div class="joker-mark">J</div>
      <div class="joker-star" aria-hidden="true">★</div>
      <div class="joker-label">JOKER</div>
    `;
    return;
  }
  const rank = RANK_LABEL[card.rank];
  const suitRef = `#suit-${card.suit}`;
  el.innerHTML = `
    <div class="corner tl">${rank}</div>
    <div class="corner tr">
      <svg class="suit-icon" aria-hidden="true"><use href="${suitRef}"/></svg>
    </div>
    <div class="pip-center">
      <svg class="pip-svg" aria-hidden="true"><use href="${suitRef}"/></svg>
    </div>
  `;
}

function elevateStockToWaste(el: HTMLElement): void {
  const restoreZ = el.style.zIndex;
  el.style.zIndex = "9999";
  window.setTimeout(() => {
    if (el.style.zIndex === "9999") el.style.zIndex = restoreZ;
  }, STOCK_TO_WASTE_ELEVATION_MS);
}
